/**
 * The receive motions of a lock form (ท่าล็อกเป้า, packages/shared/src/data/lockForms.ts): what the target's body does
 * through each beat the rules name — rocked by blows (`hit`), whirled round (`spin`), thrown back (`knock`), down on a
 * knee (`kneel`), down on the back (`fall`) and up again (`recover`, three ways: from a stagger, a knee or the back).
 * Know-how: docs/research/C0_RECEIVE.md.
 *
 * A motion group (listed in render/motions/index.ts). Every beat but the last starts and ends on `rcvStagger`, so the
 * beats chain in any order without a jump. What the rig cannot pose — the body turned flat on the ground, lifted off it,
 * the view whirled round — is keyed beside the keys in `RECEIVE_CHANNELS` and applied by the renderer
 * (render/rig/receivePlay.ts), blended with the same eases as the pose.
 *
 * Imports types only (the motion registry imports this file).
 */
import type { MotionDef, PoseRigs } from '../motions/kit';

export type Poses =
  | 'rcvBrace'
  | 'rcvSnap'
  | 'rcvReel'
  | 'rcvSag'
  | 'rcvSnap2'
  | 'rcvReel2'
  | 'rcvStagger'
  | 'rcvTwist'
  | 'rcvWhirl'
  | 'rcvWhirl2'
  | 'rcvTotter'
  | 'rcvJolt'
  | 'rcvFlung'
  | 'rcvFlungPeak'
  | 'rcvFlungDrop'
  | 'rcvLand'
  | 'rcvSkid'
  | 'rcvBuckle'
  | 'rcvDrop'
  | 'rcvKneel'
  | 'rcvKneelSlump'
  | 'rcvKneelBreath'
  | 'rcvKneelHold'
  | 'rcvTopple'
  | 'rcvTopple2'
  | 'rcvAirFlat'
  | 'rcvImpact'
  | 'rcvBounce'
  | 'rcvLie'
  | 'rcvDieSlump'
  | 'rcvDieLie'
  | 'rcvKipCoil'
  | 'rcvKipFlip'
  | 'rcvKipAir'
  | 'rcvKneelLook'
  | 'rcvPush'
  | 'rcvRiseHalf'
  | 'rcvShake'
  | 'rcvGather'
  | 'rcvSet';

export const POSES: PoseRigs<Poses> = {
  // ── hit: rocked by two blows, left staggering ──
  rcvBrace: () => {
    // The flinch before the blow lands: shoulders up, a little lower, the guard half raised.
    return { o: 4, lean: 0, tilt: 4, headDx: 1, f1: -6, f2: 4, arm: 'guard', far: 'fwd', face: 'block', sway: 0.3 };
  },
  rcvSnap: () => {
    // Contact: the head snapped back, the chest driven back over the hips, eyes shut, the front foot coming up.
    return { o: 2, lean: -2, tilt: -16, headDx: -1, f1: -6, f2: 2, l2: 2, arm: 'flail', far: 'up', face: 'block', closed: true, sway: -1.5 };
  },
  rcvReel: () => {
    // The peak of the recoil: bent furthest back, arms flung, weight on the back foot.
    return { o: 3, lean: -4, tilt: -22, headDx: -2, f1: -8, f2: 1, l2: 3, arm: 'flail', far: 'up', face: 'block', sway: -2.5, hem: 1 };
  },
  rcvSag: () => {
    // Folding forward again off the recoil, arms dropped, the head low.
    return { o: 6, lean: 0, tilt: 14, headDx: 2, f1: -7, f2: 3, arm: 'hang', far: 'hang', face: 'block', sway: 0.8 };
  },
  rcvSnap2: () => {
    // The second blow: thrown back the other way, the near arm flung out, the far one behind.
    return { o: 2, lean: -3, tilt: -14, headDx: -2, f1: -7, f2: 2, l2: 1, arm: 'out', far: 'back', face: 'block', closed: true, sway: -2 };
  },
  rcvReel2: () => {
    // Carried back by it, a step lost behind, arms trailing low.
    return { o: 4, lean: -3, tilt: -18, headDx: -1, f1: -9, f2: 2, l1: 1, arm: 'back', far: 'back', face: 'block', sway: -2 };
  },
  rcvStagger: () => {
    // Staggering (the pose every beat starts and ends on): knees soft, bent a little over, arms hanging, still facing.
    return { o: 7, lean: 0, tilt: 16, headDx: 2, f1: -8, f2: 4, arm: 'hang', far: 'hang', face: 'block', sway: 0.8 };
  },
  // ── spin: whirled round (the renderer turns the view), arms flung out by the turn ──
  rcvTwist: () => {
    // Set turning: the arms thrown out, the hems swinging up.
    return { o: 3, lean: -1, tilt: -4, headDx: -1, f1: -5, f2: 4, arm: 'out', far: 'up', face: 'block', sway: -2, hem: 2, flare: 2 };
  },
  rcvWhirl: () => {
    // Whirling on the back foot, the front foot off the ground, arms wide, the robe flared right out.
    return { o: 2, tilt: -8, headDx: -1, f1: -3, f2: 3, l2: 3, arm: 'out', far: 'up', face: 'block', closed: true, sway: -2.5, hem: 3, flare: 4 };
  },
  rcvWhirl2: () => {
    // Round onto the front foot, the back one off, still flung out.
    return { o: 2, tilt: -6, headDx: -1, f1: -3, f2: 3, l1: 3, arm: 'out', far: 'up', face: 'block', sway: -2.5, hem: 3, flare: 4 };
  },
  rcvTotter: () => {
    // Out of the turn, tottering: bent over, arms dropping, the hems settling.
    return { o: 4, lean: 1, tilt: 12, headDx: 1, f1: -6, f2: 5, arm: 'hang', far: 'back', face: 'block', sway: 1.5, hem: 1, flare: 1 };
  },
  // ── knock: struck hard, thrown back through the air (the renderer lifts it), landing in a skid ──
  rcvJolt: () => {
    // The heavy blow: arched far back, arms and feet left behind toward the striker.
    return { o: 1, lean: -4, tilt: -24, headDx: -2, f1: -5, f2: 5, l2: 3, arm: 'out', far: 'fwd', face: 'block', closed: true, sway: -2.5, hem: 1 };
  },
  rcvFlung: () => {
    // Off the ground: legs trailing forward, the body arched, hair and hems streaming forward.
    return { o: 0, lean: -3, tilt: -20, headDx: -1, f1: -2, f2: 6, l1: 4, l2: 6, arm: 'out', far: 'up', face: 'block', sway: -3, hem: 4, flare: 3 };
  },
  rcvFlungPeak: () => {
    // The top of the arc: knees drawn up, the arms flung up.
    return { o: 0, lean: -2, tilt: -14, headDx: -1, f1: -2, f2: 5, l1: 7, l2: 9, arm: 'flail', far: 'up', face: 'block', sway: -3, hem: 4, flare: 4 };
  },
  rcvFlungDrop: () => {
    // Coming down: legs reaching for the ground, arms out for balance.
    return { o: 0, lean: -1, tilt: -6, f1: -4, f2: 5, l1: 3, l2: 3, arm: 'out', far: 'back', face: 'block', sway: -1, hem: 5, flare: 3 };
  },
  rcvLand: () => {
    // Down hard: a deep crouch on wide feet, folded over, one arm out to keep from going over.
    return { o: 9, lean: 0, tilt: 18, headDx: 1, f1: -9, f2: 6, arm: 'out', far: 'back', face: 'block', sway: 1, hem: 2, flare: 1 };
  },
  rcvSkid: () => {
    // Skidding to a stop: still low, the back foot braking, rising out of the crouch.
    return { o: 7, lean: -1, tilt: 12, headDx: 1, f1: -10, f2: 5, arm: 'low', far: 'back', face: 'block', sway: 0.5, hem: 1 };
  },
  // ── kneel: the knees give, down on one knee, head bowed ──
  rcvBuckle: () => {
    // The knees giving way, the body folding over them.
    return { o: 8, lean: 0, tilt: 14, headDx: 1, f1: -7, f2: 5, arm: 'hang', far: 'hang', face: 'block', closed: true, sway: 1 };
  },
  rcvDrop: () => {
    // Dropping: lower, the back knee going down, a hand reaching for the ground.
    return { o: 12, lean: 0, tilt: 20, headDx: 1, f1: -8, f2: 6, arm: 'low', far: 'hang', face: 'block', closed: true, sway: 1.5, hem: 1 };
  },
  rcvKneel: () => {
    // The knee hits the ground: lowest, folded over the front knee.
    return { o: 16, lean: 1, tilt: 24, headDx: 1, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'block', closed: true, sway: 1.5 };
  },
  rcvKneelSlump: () => {
    // Slumped over it a moment, the head bowed.
    return { o: 17, lean: 1, tilt: 27, headDx: 1, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'block', closed: true, sway: 1 };
  },
  rcvKneelBreath: () => {
    // A breath: the back lifting a little, a hand on the knee.
    return { o: 16, lean: 0, tilt: 19, headDx: 1, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'block', sway: 0.5 };
  },
  rcvKneelHold: () => {
    // Held on the knee, bowed, gathering breath (where the kneel beat ends).
    return { o: 16, lean: 0, tilt: 22, headDx: 1, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'block', closed: true, sway: 0.5 };
  },
  // ── fall: toppling onto the back (the renderer lays the figure down), a bounce, lying still ──
  rcvTopple: () => {
    // Going over backwards: the front foot kicked up, the arms thrown forward.
    return { o: 2, lean: -3, tilt: -18, headDx: -1, f1: -6, f2: 6, l2: 5, arm: 'forward', far: 'fwd', face: 'block', closed: true, sway: -2, hem: 2, flare: 1 };
  },
  rcvTopple2: () => {
    // Further over: both knees bending, the arms up.
    return { o: 4, lean: -2, tilt: -14, headDx: -1, f1: -5, f2: 6, l1: 2, l2: 8, arm: 'flail', far: 'up', face: 'block', closed: true, sway: -2.5, hem: 3, flare: 2 };
  },
  rcvAirFlat: () => {
    // Nearly flat in the air: knees up, arms out.
    return { o: 4, lean: -1, tilt: -8, f1: -3, f2: 6, l1: 4, l2: 9, arm: 'out', far: 'up', face: 'block', closed: true, sway: -2, hem: 4, flare: 2 };
  },
  rcvImpact: () => {
    // The back hits the ground: arms slapped out, legs straightening, eyes shut.
    return { o: 2, tilt: -4, f1: -4, f2: 5, l2: 2, arm: 'out', far: 'up', face: 'block', closed: true, sway: 1, hem: 1, flare: 2 };
  },
  rcvBounce: () => {
    // The small bounce off the ground: the knees and arms lifting a little.
    return { o: 3, tilt: -6, f1: -3, f2: 5, l1: 1, l2: 4, arm: 'flail', far: 'up', face: 'block', closed: true, sway: -1, hem: 2, flare: 2 };
  },
  rcvLie: () => {
    // Lying on the back, one knee raised, arms out (where the fall beat ends).
    return { o: 2, tilt: -4, f1: -4, f2: 5, l2: 3, arm: 'out', far: 'up', face: 'block', closed: true, sway: 0.5, hem: 1, flare: 1 };
  },
  // ── death (hitReact.ts, not a lock-form beat): the knees give first, then over backwards ──
  rcvDieSlump: () => {
    // Down on the knees, the body sagging back off them, head dropping, arms hanging slack.
    return { o: 13, lean: -1, tilt: -6, headDx: 1, f1: -6, f2: 6, l1: 2, arm: 'hang', far: 'hang', face: 'block', closed: true, sway: 1, hem: 1 };
  },
  rcvDieLie: () => {
    // Lying still on the back, not a plank: the near knee bent up, the head lolled to the side, one arm out.
    return { o: 3, tilt: -2, headDx: 2, f1: -3, f2: 6, l1: 1, l2: 9, arm: 'out', far: 'hang', face: 'block', closed: true, sway: 0.5, hem: 2, flare: 2 };
  },
  // ── recover: up again, three ways ──
  rcvKipCoil: () => {
    // The carp leap (鲤鱼打挺) gathered: knees drawn to the chest, hands by the head.
    return { o: 6, tilt: 10, headDx: 1, f1: -1, f2: 4, l1: 6, l2: 8, arm: 'raise', far: 'up', face: 'atk', sway: 1, hem: 2 };
  },
  rcvKipFlip: () => {
    // The legs whipped out and down, the body arching up off the ground.
    return { o: 2, tilt: -16, headDx: -1, f1: -5, f2: 6, l1: 3, l2: 4, arm: 'flail', far: 'up', face: 'atk', sway: -2, hem: 4, flare: 3 };
  },
  rcvKipAir: () => {
    // In the air, coming upright, the feet under the body.
    return { o: 1, tilt: -4, f1: -5, f2: 5, l1: 2, l2: 3, arm: 'out', far: 'back', face: 'atk', sway: -1.5, hem: 4, flare: 2 };
  },
  rcvKneelLook: () => {
    // Still on the knee, the head coming up to the foe.
    return { o: 16, lean: 0, tilt: 12, headDx: 0, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'block', sway: 0.3 };
  },
  rcvPush: () => {
    // A hand on the front knee, pushing up off the ground.
    return { o: 12, lean: 1, tilt: 18, headDx: 1, f1: -8, f2: 7, arm: 'low', far: 'hang', face: 'atk', sway: 0.8 };
  },
  rcvRiseHalf: () => {
    // Half up, the guard coming back.
    return { o: 6, lean: 0, tilt: 8, f1: -7, f2: 5, arm: 'guard', far: 'fwd', face: 'block', sway: 0.5 };
  },
  rcvShake: () => {
    // The head cleared with a shake, the body coming upright.
    return { o: 4, lean: -1, tilt: 2, headDx: -1, f1: -7, f2: 4, arm: 'hang', far: 'hang', face: 'block', sway: -0.5 };
  },
  rcvGather: () => {
    // Gathering: the guard raised, the feet set again.
    return { o: 4, lean: 0, tilt: 4, f1: -6, f2: 5, arm: 'guard', far: 'fwd', face: 'block', sway: 0.3 };
  },
  rcvSet: () => {
    // Set in the stance once more, the weapon arm finding its place (eases into `ready`).
    return { o: 3, lean: 1, tilt: 5, f1: -6, f2: 5, arm: 'cut', far: 'fwd', face: 'atk', sway: 0 };
  },
};

/** The receive motions, one per beat (and three recovers). Keys counted in the comments. */
export const MOTIONS = [
  /** Rocked by two blows, left staggering (8 keys). */
  {
    id: 'rcvHit',
    keys: ['ready', 'rcvBrace', 'rcvSnap', 'rcvReel', 'rcvSag', 'rcvSnap2', 'rcvReel2', 'rcvStagger'],
    ease: ['inOut', 'out', 'out', 'inOut', 'out', 'out', 'inOut'],
  },
  /** Whirled round twice, tottering out of it (8 keys; the view turns, `RECEIVE_CHANNELS`). */
  {
    id: 'rcvSpin',
    keys: ['rcvStagger', 'rcvTwist', 'rcvWhirl', 'rcvWhirl2', 'rcvWhirl', 'rcvTwist', 'rcvTotter', 'rcvStagger'],
    ease: ['out', 'linear', 'linear', 'linear', 'linear', 'out', 'inOut'],
  },
  /** Struck hard, thrown back through the air, landing in a skid (9 keys; lifted, `RECEIVE_CHANNELS`). */
  {
    id: 'rcvKnock',
    keys: ['rcvStagger', 'rcvJolt', 'rcvFlung', 'rcvFlungPeak', 'rcvFlungDrop', 'rcvLand', 'rcvSkid', 'rcvSag', 'rcvStagger'],
    ease: ['out', 'out', 'out', 'in', 'in', 'out', 'inOut', 'inOut'],
  },
  /** The knees give, down on one knee, bowed (8 keys). */
  {
    id: 'rcvKneel',
    keys: ['rcvStagger', 'rcvBuckle', 'rcvDrop', 'rcvKneel', 'rcvKneelSlump', 'rcvKneelBreath', 'rcvKneelHold', 'rcvKneelHold'],
    ease: ['in', 'in', 'in', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Over backwards onto the ground, a bounce, lying still (9 keys; laid down, `RECEIVE_CHANNELS`). */
  {
    id: 'rcvFall',
    keys: ['rcvStagger', 'rcvTopple', 'rcvTopple2', 'rcvAirFlat', 'rcvImpact', 'rcvBounce', 'rcvImpact', 'rcvLie', 'rcvLie'],
    ease: ['out', 'in', 'in', 'in', 'out', 'in', 'inOut', 'inOut'],
  },
  /**
   * Death (render/hitReact.ts, ns-art HIT-REACT death: knees first, then the torso, no plank on the ground): the knees
   * buckle, down on them, sagging back, over backwards, the impact and a bounce, lying with a knee up (10 keys).
   */
  {
    id: 'rcvDie',
    keys: ['rcvStagger', 'rcvBuckle', 'rcvDrop', 'rcvDieSlump', 'rcvTopple2', 'rcvAirFlat', 'rcvImpact', 'rcvBounce', 'rcvDieLie', 'rcvDieLie'],
    ease: ['in', 'inOut', 'out', 'in', 'in', 'in', 'out', 'inOut', 'inOut'],
  },
  /** Up from a stagger: the head cleared, the guard raised, back in the stance (6 keys). */
  {
    id: 'rcvUp',
    keys: ['rcvStagger', 'rcvSag', 'rcvShake', 'rcvGather', 'rcvSet', 'ready'],
    ease: ['inOut', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Up from a knee: the head up, a push off the knee, half up, set (7 keys). */
  {
    id: 'rcvUpKneel',
    keys: ['rcvKneelHold', 'rcvKneelLook', 'rcvPush', 'rcvRiseHalf', 'rcvGather', 'rcvSet', 'ready'],
    ease: ['inOut', 'inOut', 'out', 'inOut', 'inOut', 'inOut'],
  },
  /** Up from the back with the carp leap: coiled, the legs whipped, up through the air, landing set (8 keys). */
  {
    id: 'rcvUpKip',
    keys: ['rcvLie', 'rcvKipCoil', 'rcvKipFlip', 'rcvKipAir', 'rcvLand', 'rcvGather', 'rcvSet', 'ready'],
    ease: ['inOut', 'out', 'out', 'in', 'out', 'inOut', 'inOut'],
  },
] as const satisfies readonly MotionDef[];

export type ReceiveMotionId = (typeof MOTIONS)[number]['id'];

/**
 * What the renderer does to the shot at each key (one value per key, blended like the pose): `rot` turns the figure about
 * its feet in its own unmirrored frame, which faces right (negative = over backwards); `lift` raises it off the ground (art
 * pixels); `turn` whirls the view round (45° heading steps; a whole number of turns by the last key).
 */
export interface ReceiveChannels {
  rot?: readonly number[];
  lift?: readonly number[];
  turn?: readonly number[];
}

const FLAT = -Math.PI / 2;

export const RECEIVE_CHANNELS: Readonly<Partial<Record<ReceiveMotionId, ReceiveChannels>>> = {
  rcvSpin: { turn: [0, 2, 5, 8, 11, 14, 16, 16] },
  rcvKnock: { lift: [0, 2, 8, 12, 6, 0, 0, 0, 0] },
  rcvFall: {
    rot: [0, -0.25, -0.75, -1.3, FLAT, FLAT + 0.1, FLAT, FLAT, FLAT],
    lift: [0, 1, 4, 3, 0, 2, 0, 0, 0],
  },
  rcvDie: {
    rot: [0, 0, 0, -0.12, -0.6, -1.25, FLAT, FLAT + 0.1, FLAT, FLAT],
    lift: [0, 0, 0, 0, 1, 2, 0, 1, 0, 0],
  },
  rcvUpKip: {
    rot: [FLAT, FLAT + 0.1, -0.9, -0.2, 0, 0, 0, 0],
    lift: [0, 0, 5, 7, 0, 0, 0, 0],
  },
};
